import { updatePageProps } from '@eventbrite/datalayer-library';
import {
    hasUrgencySignalsEnabled,
    UrgencySignalsProps,
} from '@eventbrite/urgency-signals';
import { EventDescriptionProps } from '../components/EventDetails/EventDescription/EventDescription';
import { FaqItem } from '../components/EventDetails/Widgets/Faq';
import { Event, EventDetailsProps, EventHeroScopedProps, User } from '../types';

const hasWidgetType = (
    widgetType: String,
    eventDescription?: EventDescriptionProps,
) => {
    return eventDescription?.structuredContent?.widgets.some(
        (widget) => widget.type === widgetType,
    );
};

const getFaqsData = (eventDescription?: EventDescriptionProps): FaqItem[] => {
    const faqWidget = eventDescription?.structuredContent?.widgets.find(
        (widget) => widget.type === 'faqs',
    )?.data || { faqs: [] };
    return 'faqs' in faqWidget ? faqWidget?.faqs : [];
};

const hasVideoModule = (eventDescription?: EventDescriptionProps): Boolean => {
    return !!eventDescription?.structuredContent?.modules.find(
        (module) => module.type === 'video',
    );
};

const normalizeCategory = (category: string) => {
    return category.toLocaleLowerCase().replace(/\s/g, '_');
};

export const dataLayerMiddleware = (
    event?: Event,
    components?: {
        eventDescription?: EventDescriptionProps;
        eventDetails?: EventDetailsProps;
        eventHero?: EventHeroScopedProps;
    },
    urgencySignals?: UrgencySignalsProps,
    user?: User,
) => {
    const eventDescription = components?.eventDescription;
    const eventDetails = components?.eventDetails;
    const eventHero = components?.eventHero;
    const hasHeroVideo = !!components?.eventHero?.featuredVideo?.url;
    const page = {
        eventId: event?.id,
        isPaid: eventDetails?.refund?.isPaidEvent,
        isFree: !eventDetails?.refund.isPaidEvent,
        isOnline: event?.isOnlineEvent,
        isPrivate: event?.isProtected,
        isListed: event?.isListed,
        isLive: event?.isLive,
        isRepeatingEvent: event?.isSeriesParent && !!event?.childEvents,
        isSeriesParent: event?.isSeriesParent,
        eventStatus: event?.salesStatus.sales_status,
        hasAutogeneratedContent:
            !!eventDescription?.structuredContent?.hasAutogeneratedContent,
        hasUrgencySignals: hasUrgencySignalsEnabled(urgencySignals),
        hasEarlyBirdUrgencySignal: urgencySignals?.categories.earlyBird,
        hasNewUrgencySignal: urgencySignals?.categories.new,
        hasPopularUrgencySignal: urgencySignals?.categories.popular,
        hasFewTicketsLeftUrgencySignal: urgencySignals?.messages.fewTickets,
        hasSalesEndSoonUrgencySignal: urgencySignals?.messages.salesEndSoon,
        eventStartDate: event?.start.utc,
        eventEndDate: event?.end.utc,
        hasImages: eventHero?.items?.length,
        hasRSVP: event?.shouldDisplayRsvp,
        hasAgenda: hasWidgetType('agenda', eventDescription),
        hasFaqs: hasWidgetType('faqs', eventDescription),
        hasVideo: hasVideoModule(eventDescription) || hasHeroVideo,
        heroVideo: hasHeroVideo,
        faqsNumber: getFaqsData(eventDescription)?.length,
        format: event?.format ? normalizeCategory(event.format) : '',
        category: event?.category ? normalizeCategory(event.category) : '',
        subcategory: event?.subcategory
            ? normalizeCategory(event.subcategory)
            : '',
        isAuthenticated: user?.isAuthenticated,
    };

    updatePageProps({ page });
};
